import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  Controller,
  ControllerWrapper,
  costFormater,
  DateInput,
  DeductibleType,
  FieldWrapper,
  FormFieldsContainer,
  Inscription,
  Label,
  PriceInput,
  useFormContext,
} from '@common-fe/common-fe';
import dayjs from 'dayjs';
import _ from 'lodash';
import debounce from 'lodash/debounce';
import styled from 'styled-components';

import { AccumulatedItem } from '@/modules/deductibles/SpendTracker/hooks/useDeductibles.ts';
import { Dependent } from '@/modules/employee/employee.types';

import { DeductibleFormField, DeductibleFormFields } from './hooks/useDeductibleForm';
import { DeductiblePreviewPayload, DeductiblePreviewResponse } from './queries/useDeductiblePreview.query';
import {
  getMaxDeductibleError,
  getMemberKey,
  getTierInfoMessage,
  isEmptyValue,
  isZeroEmptyTransition,
  mapIndividualPreview,
  MAX_AMOUNT_ERROR_MESSAGE,
  MAX_INDIVIDUAL_AMOUNT_ERROR_MESSAGE,
  parseMaxIndividualFromMessage,
} from './utils/deductibleModalUtils';
import { DeductibleErrorInfo } from './DeductibleErrorInfo';
import { IndividualDeductibleFields } from './IndividualDeductibleFields';

const isIndividualEmbeddedError = (error: string | null) =>
  !!error &&
  error.includes('IndividualDeductibleAmount cannot be greater than maximum embedded trigger amount');

const DEBOUNCE_MS = 400;
export const PriceControl = styled((props) => <PriceInput {...props} />)`
  width: 160px;
  max-height: 40px;
`;

const DeductibleForm: React.FC<{
  mode: DeductibleType;
  healthPolicyId?: string;
  members?: Array<
    Dependent | { dependentId: string; employeeId: string; fullName: string; relationshipType: string }
  >;
  familyValue?: number;
  individualAccumulatedValues?: AccumulatedItem[];
  employeeId?: string;
  serverError: string | null;
  setServerError: (msg: string | null) => void;
  onSubmit: (payload: DeductiblePreviewPayload) => Promise<DeductiblePreviewResponse>;
}> = ({
  mode,
  healthPolicyId,
  individualAccumulatedValues = [],
  members,
  employeeId,
  serverError,
  setServerError,
  onSubmit,
}) => {
  const {
    control,
    formState: { errors },
    clearErrors,
    getValues,
    setValue,
  } = useFormContext<DeductibleFormFields>();

  const [individualPreview, setIndividualPreview] = useState<
    Record<string, { tier?: string; is_tier_changed?: boolean }>
  >({});
  const [infoMessage, setInfoMessage] = useState<string | null>(null);
  const [maxIndividualAmount, setMaxIndividualAmount] = useState<number | null>(null);

  const lastFamilyValueRef = useRef<string | null>(null);
  const lastIndividualValuesRef = useRef<Record<string, string>>({});
  const latestOnSubmitRef = useRef(onSubmit);

  const updateFamilyDeductibleSum = useCallback(() => {
    const memberDeductible = getValues(DeductibleFormField.memberDeductible) || {};
    const individualSum = Object.values(memberDeductible)
      .map((v) => Number(v) || 0)
      .reduce((acc, v) => acc + v, 0);

    setValue(DeductibleFormField.familyDeductible, individualSum.toFixed(2));
    if (errors?.familyDeductible || serverError || infoMessage) {
      clearErrors(DeductibleFormField.familyDeductible);
      setServerError(null);
      setInfoMessage(null);
      setMaxIndividualAmount(null);
    }
  }, [setValue, getValues, errors, serverError, infoMessage, clearErrors, setServerError]);

  const handleError = useCallback(
    (e: unknown) => {
      const message = _.get(e, 'response.data.elevate_error_message', '') as string;
      if (message.includes(MAX_INDIVIDUAL_AMOUNT_ERROR_MESSAGE)) {
        const max = parseMaxIndividualFromMessage(message);
        setMaxIndividualAmount(max);
        setServerError(message);
      } else if (message.includes(MAX_AMOUNT_ERROR_MESSAGE)) {
        setMaxIndividualAmount(null);
        setServerError(getMaxDeductibleError(message, costFormater));
      } else {
        setMaxIndividualAmount(null);
        setServerError(message);
      }
    },
    [setServerError]
  );

  const handleTierInfo = useCallback((response: DeductiblePreviewResponse) => {
    if (response.is_tier_changed) {
      setInfoMessage(getTierInfoMessage(response?.family_tier));
    } else {
      setInfoMessage(null);
    }
  }, []);

  const debouncedFamilyValueChange = useRef(
    debounce(async (value: string) => {
      setServerError(null);
      setInfoMessage(null);
      if (isEmptyValue(value)) return;
      try {
        const response = await latestOnSubmitRef.current({
          health_policy_id: _.toNumber(healthPolicyId),
          family_deductible_amount: _.toNumber(value),
          individual_deductibles: [
            {
              employee_id: _.toNumber(employeeId),
              deductibles_amount: _.toNumber(value),
            },
          ],
        });

        clearErrors(DeductibleFormField.familyDeductible);
        setServerError(null);
        setInfoMessage(null);
        setMaxIndividualAmount(null);
        handleTierInfo(response);
      } catch (e) {
        handleError(e);
      }
    }, DEBOUNCE_MS)
  ).current;

  const individualValueDebouncedChange = useRef(
    debounce(async (key: string, value: string) => {
      if (isEmptyValue(value)) return;
      try {
        const allValues = getValues(DeductibleFormField.memberDeductible) || {};
        allValues[key] = value;
        const individual_deductibles = individualAccumulatedValues.map((item) => {
          const id = getMemberKey(item);
          return {
            employee_id: _.toNumber(item.employeeId),
            dependent_id: item.dependentId ? _.toNumber(item.dependentId) : undefined,
            deductibles_amount: id != null ? _.toNumber(allValues[id]) : 0,
          };
        });

        const response = await latestOnSubmitRef.current({
          health_policy_id: _.toNumber(healthPolicyId),
          family_deductible_amount: _.toNumber(getValues(DeductibleFormField.familyDeductible)),
          individual_deductibles,
        });

        handleTierInfo(response);
        setMaxIndividualAmount(null);

        if (Array.isArray(response.individual_deductibles)) {
          setIndividualPreview((prev) => ({
            ...prev,
            ...mapIndividualPreview(response.individual_deductibles),
          }));
        }
      } catch (e) {
        setIndividualPreview({});
        handleError(e);
      }
    }, DEBOUNCE_MS)
  ).current;

  useEffect(() => {
    latestOnSubmitRef.current = onSubmit;
  }, [onSubmit]);
  useEffect(() => {
    return () => {
      debouncedFamilyValueChange.cancel();
      individualValueDebouncedChange.cancel();
    };
  }, [debouncedFamilyValueChange, individualValueDebouncedChange]);

  return (
    <FormFieldsContainer gap="m" data-testid="deductible-form">
      {/* Block 1: Effective date */}
      <Box
        background="canvas"
        round="container1Round"
        pad="spacing24"
        border={{ size: '1px', color: 'border2' }}
      >
        <FieldWrapper>
          <Label name={DeductibleFormField.effectiveDate} isNarrow>
            Effective date
          </Label>
          <ControllerWrapper errorText={errors?.effectiveDate?.message} style={{ maxWidth: '160px' }}>
            <Controller
              name={DeductibleFormField.effectiveDate}
              control={control}
              render={({ field }) => (
                <DateInput
                  isPicker
                  isWide
                  isError={!!errors?.effectiveDate?.message}
                  maxDate={dayjs()}
                  testId="effective-date"
                  placeholder="Select date"
                  value={field.value || ''}
                  onChange={field.onChange}
                />
              )}
            />
          </ControllerWrapper>
        </FieldWrapper>
      </Box>

      {/* Block 2: Family Deductible Amount */}
      <Box
        background="canvas"
        round="container1Round"
        pad="spacing24"
        border={{ size: '1px', color: 'border2' }}
      >
        <FieldWrapper>
          <Label name={DeductibleFormField.familyDeductible} isNarrow>
            Total family deductible
          </Label>
          <ControllerWrapper>
            <Controller
              name={DeductibleFormField.familyDeductible}
              control={control}
              render={({ field }) =>
                mode === DeductibleType.Family ? (
                  <PriceControl
                    {...field}
                    errorText={errors?.familyDeductible?.message}
                    onChange={(value: string) => {
                      const prevValue = lastFamilyValueRef.current;
                      const normalized = _.toNumber(value);
                      const lastNormalized = _.toNumber(prevValue);
                      const isZeroFromEmpty = isZeroEmptyTransition(prevValue, value);

                      field.onChange(value);

                      if (normalized !== lastNormalized || isZeroFromEmpty) {
                        lastFamilyValueRef.current = value;
                        debouncedFamilyValueChange(value);
                      }
                    }}
                  />
                ) : (
                  <Inscription
                    lineHeight="20px"
                    color={serverError && !isIndividualEmbeddedError(serverError) ? 'danger' : 'textBody'}
                  >
                    {costFormater(+field.value, true)}
                  </Inscription>
                )
              }
            />
          </ControllerWrapper>
        </FieldWrapper>
        <DeductibleErrorInfo
          serverError={isIndividualEmbeddedError(serverError) ? null : serverError}
          infoMessage={infoMessage}
        />
        {/* Block 3: Individual Deductibles Amount */}
        {mode === DeductibleType.Individual && (
          <>
            <Box height="1px" background="border1" margin={{ vertical: 'm' }} />
            <Box gap="xs">
              <IndividualDeductibleFields
                members={members}
                individualAccumulatedValues={individualAccumulatedValues}
                errors={errors}
                control={control}
                clearErrors={clearErrors}
                individualPreview={individualPreview}
                setIndividualPreview={setIndividualPreview}
                lastIndividualValuesRef={lastIndividualValuesRef}
                individualValueDebouncedChange={individualValueDebouncedChange}
                updateFamilyDeductibleSum={updateFamilyDeductibleSum}
                maxIndividualAmount={maxIndividualAmount}
              />
            </Box>
          </>
        )}
      </Box>
    </FormFieldsContainer>
  );
};

export default DeductibleForm;
